import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Alert,
  AlertTitle,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNote";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import { useNavigate } from "react-router-dom";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import AcademicCountdown from "../../DynamicComponent/AcademicCountdown";

const FONT = "'Inter', 'Roboto', sans-serif";

function formatTime(isoString) {
  if (!isoString) return "—";
  try {
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return isoString;
  }
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function StaffDashboard() {
  const navigate = useNavigate();

  const [checkinTime, setCheckinTime] = useState(null);
  const [checkoutTime, setCheckoutTime] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkedOut, setCheckedOut] = useState(false);
  const [missedDays, setMissedDays] = useState(0);
  const [kpi, setKpi] = useState({
    todayStatus: "—",
    monthAttendance: "—",
    pendingLeaves: 0,
  });
  const [loading, setLoading] = useState({ in: false, out: false });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [now, setNow] = useState(new Date());

  // live clock
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetchData("staff/dashboard");
      const d = res?.data || {};
      setCheckinTime(d.todayCheckin || null);
      setCheckoutTime(d.todayCheckout || null);
      setCheckedIn(Boolean(d.todayCheckin));
      setCheckedOut(Boolean(d.todayCheckout));
      setMissedDays(d.consecutiveMissedDays || 0);
      setKpi({
        todayStatus: d.todayStatus || (d.todayCheckin ? "Present" : "Absent"),
        monthAttendance: d.monthAttendance || "0%",
        pendingLeaves: d.pendingLeaves || 0,
      });
    } catch {
      // fallback: try to read today's record separately
      try {
        const r = await fetchData(`staff/checkin/today?date=${todayISO()}`);
        const d = r?.data || {};
        setCheckinTime(d.checkinTime || null);
        setCheckoutTime(d.checkoutTime || null);
        setCheckedIn(Boolean(d.checkinTime));
        setCheckedOut(Boolean(d.checkoutTime));
        setMissedDays(d.consecutiveMissedDays || 0);
      } catch {
        // silent
      }
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleCheckIn = async () => {
    setLoading((l) => ({ ...l, in: true }));
    try {
      const res = await postData("staff/checkin", { date: todayISO() });
      const time = res?.data?.checkinTime || new Date().toISOString();
      setCheckinTime(time);
      setCheckedIn(true);
      setKpi((k) => ({ ...k, todayStatus: "Present" }));
      setToast({ open: true, severity: "success", message: `Checked in at ${formatTime(time)}` });
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Check-in failed. Please try again." });
    } finally {
      setLoading((l) => ({ ...l, in: false }));
    }
  };

  const handleCheckOut = async () => {
    setLoading((l) => ({ ...l, out: true }));
    try {
      const res = await postData("staff/checkout", { date: todayISO() });
      const time = res?.data?.checkoutTime || new Date().toISOString();
      setCheckoutTime(time);
      setCheckedOut(true);
      setToast({ open: true, severity: "success", message: `Checked out at ${formatTime(time)}` });
    } catch {
      setToast({ open: true, severity: "error", message: "Check-out failed. Please try again." });
    } finally {
      setLoading((l) => ({ ...l, out: false }));
    }
  };

  const staffName =
    sessionStorage.getItem("userFullName") ||
    sessionStorage.getItem("userName") ||
    "Staff";

  const kpiCards = [
    {
      label: "Today's Status",
      value: kpi.todayStatus,
      color: kpi.todayStatus === "Present" ? "#4caf50" : kpi.todayStatus === "Absent" ? "#f44336" : "#ff9800",
      bg: kpi.todayStatus === "Present" ? "#e8f5e9" : kpi.todayStatus === "Absent" ? "#ffebee" : "#fff3e0",
      icon: <CheckCircleOutlineIcon />,
    },
    {
      label: "This Month Attendance",
      value: kpi.monthAttendance,
      color: "#244C77",
      bg: "#EAF1F8",
      icon: <CalendarTodayIcon />,
    },
    {
      label: "Pending Leaves",
      value: kpi.pendingLeaves,
      color: "#ff9800",
      bg: "#fff3e0",
      icon: <EventNoteIcon />,
    },
    {
      label: "Today's Date",
      value: now.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
      subValue: now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
      color: "#2196f3",
      bg: "#e3f2fd",
      icon: <AccessTimeIcon />,
    },
  ];

  return (
    <Box
      sx={{
        padding: "1.5rem",
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        fontFamily: FONT,
      }}
    >
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <AcademicCountdown />

      {/* Welcome Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "#1a1a2e",
            fontFamily: FONT,
          }}
        >
          Hi {staffName},{" "}
          <span
            style={{
              background: "linear-gradient(90deg, #4A7BA8, #244C77)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Welcome!
          </span>
        </Typography>
        <Typography sx={{ color: "#8F95B2", fontSize: "13px", fontFamily: FONT }}>
          {now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </Typography>
      </Box>

      {/* Consecutive-miss warning banner */}
      {missedDays >= 2 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #fde68a",
            "& .MuiAlert-message": { width: "100%" },
          }}
          action={
            <Button
              size="small"
              variant="contained"
              startIcon={<AssignmentLateIcon />}
              onClick={() => navigate("staff-mandate-leave")}
              sx={{
                backgroundColor: "#f59e0b",
                color: "#fff",
                textTransform: "none",
                borderRadius: "8px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#d97706" },
              }}
            >
              Fill Mandatory Leave Form
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: "bold" }}>Attendance Alert</AlertTitle>
          You have not checked in for more than{" "}
          <strong>{missedDays} consecutive days</strong>. Please complete the
          Mandatory Leave Form.
        </Alert>
      )}

      {/* Check-In / Check-Out Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          mb: 3,
          overflow: "hidden",
          boxShadow: "0 2px 16px rgba(80,56,237,0.10)",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
            px: 3,
            py: 2,
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: "bold",
              fontSize: "15px",
              fontFamily: FONT,
            }}
          >
            Daily Attendance
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>
            Record your attendance for today
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Check-In button */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<CheckCircleOutlineIcon />}
                  disabled={checkedIn || loading.in}
                  onClick={handleCheckIn}
                  sx={{
                    backgroundColor: "#4caf50",
                    color: "#fff",
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "16px",
                    py: 2,
                    boxShadow: "0 4px 14px rgba(76,175,80,0.35)",
                    "&:hover": { backgroundColor: "#43a047" },
                    "&.Mui-disabled": {
                      backgroundColor: "#c8e6c9",
                      color: "#fff",
                    },
                  }}
                >
                  {loading.in ? "Checking In…" : checkedIn ? "Checked In" : "Check-In"}
                </Button>
                {checkinTime && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: "13px",
                      color: "#4caf50",
                      fontWeight: 600,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: "14px", mr: 0.4, verticalAlign: "middle" }} />
                    {formatTime(checkinTime)}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Check-Out button */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<LogoutIcon />}
                  disabled={!checkedIn || checkedOut || loading.out}
                  onClick={handleCheckOut}
                  sx={{
                    backgroundColor: "#f44336",
                    color: "#fff",
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "16px",
                    py: 2,
                    boxShadow: "0 4px 14px rgba(244,67,54,0.30)",
                    "&:hover": { backgroundColor: "#e53935" },
                    "&.Mui-disabled": {
                      backgroundColor: "#ffcdd2",
                      color: "#fff",
                    },
                  }}
                >
                  {loading.out ? "Checking Out…" : checkedOut ? "Checked Out" : "Check-Out"}
                </Button>
                {checkoutTime && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: "13px",
                      color: "#f44336",
                      fontWeight: 600,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: "14px", mr: 0.4, verticalAlign: "middle" }} />
                    {formatTime(checkoutTime)}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Mandatory Leave Form shortcut */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AssignmentLateIcon />}
                  onClick={() => navigate("staff-mandate-leave")}
                  sx={{
                    borderColor: "#244C77",
                    color: "#244C77",
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    py: 1.8,
                    "&:hover": { borderColor: "#244C77", color: "#244C77", backgroundColor: "#f0edff" },
                  }}
                >
                  Mandatory Leave Form
                </Button>
                <Typography sx={{ mt: 1, fontSize: "11px", color: "#8F95B2" }}>
                  For absences of 2+ days
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Status summary chips */}
          {(checkinTime || checkoutTime) && (
            <Box sx={{ display: "flex", gap: 1.5, mt: 2.5, flexWrap: "wrap" }}>
              {checkinTime && (
                <Chip
                  icon={<CheckCircleOutlineIcon sx={{ fontSize: "16px !important" }} />}
                  label={`Check-In: ${formatTime(checkinTime)}`}
                  size="small"
                  sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: 600, fontSize: "12px" }}
                />
              )}
              {checkoutTime && (
                <Chip
                  icon={<LogoutIcon sx={{ fontSize: "16px !important" }} />}
                  label={`Check-Out: ${formatTime(checkoutTime)}`}
                  size="small"
                  sx={{ backgroundColor: "#ffebee", color: "#f44336", fontWeight: 600, fontSize: "12px" }}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 2,
                height: "100%",
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: card.bg,
                  color: card.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {card.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "22px",
                    fontWeight: "bold",
                    color: "#1a1a2e",
                    lineHeight: 1.1,
                    fontFamily: FONT,
                  }}
                >
                  {card.value}
                </Typography>
                {card.subValue && (
                  <Typography sx={{ fontSize: "11px", color: card.color, fontWeight: 600 }}>
                    {card.subValue}
                  </Typography>
                )}
                <Typography sx={{ fontSize: "12px", color: "#8F95B2", fontFamily: FONT }}>
                  {card.label}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Academic Countdown is already at top */}
    </Box>
  );
}
